import React from 'react';
import { Typography, Tooltip } from 'antd';
import { STAGE_CONFIG, StageType, STAGE_CONFIG as SC } from '../../../../../types/healthcare/TreatmentPlanTypes';
import { GroupOutlined } from '@ant-design/icons';
import { getStageNodeIcon } from '../../../../../utils/healthcare/treatmentPlanIcons';

const { Text } = Typography;

const SECTION_COLOR = '#4A90D9';

interface NodePaletteProps {
  onDragStart: (event: React.DragEvent, nodeType: StageType | 'section_group') => void;
}

const NodePalette: React.FC<NodePaletteProps> = ({ onDragStart }) => {
  return (
    <div style={{ padding: '16px 12px' }}>
      <Text
        strong
        style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 12 }}
      >
        Node Types
      </Text>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Tooltip title="Group stages into a named phase" placement="right">
          <div
            draggable
            onDragStart={(e) => onDragStart(e, 'section_group')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 8,
              border: `1.5px solid ${SECTION_COLOR}22`,
              background: `${SECTION_COLOR}12`,
              cursor: 'grab',
              userSelect: 'none',
              transition: 'background 0.15s, transform 0.1s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = `${SECTION_COLOR}22`;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = `${SECTION_COLOR}12`;
            }}
          >
            <span style={{ color: SECTION_COLOR, fontSize: 16 }}><GroupOutlined /></span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 12 }}>Section Group</div>
              <div style={{ fontSize: 10, color: 'var(--mg-text-secondary)' }}>Group stages into a named phase</div>
            </div>
          </div>
        </Tooltip>

        {/* admin_review is admin-only — it is added in the MyGene admin app, never in the clinic builder. */}
        {(Object.keys(SC) as StageType[]).filter((type) => type !== 'admin_review').map((type) => {
          const cfg = STAGE_CONFIG[type];
          return (
            <Tooltip key={type} title={cfg.description} placement="right">
              <div
                draggable
                onDragStart={(e) => onDragStart(e, type)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: `1.5px solid ${cfg.color}22`,
                  background: `${cfg.color}12`,
                  cursor: 'grab',
                  userSelect: 'none',
                  transition: 'background 0.15s, transform 0.1s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = `${cfg.color}22`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = `${cfg.color}12`;
                }}
              >
                <span style={{ color: cfg.color, fontSize: 16 }}>{getStageNodeIcon(type)}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{cfg.label}</div>
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>

      <div style={{ marginTop: 20, padding: '10px', borderRadius: 8, fontSize: 11, background: 'var(--mg-bg-subtle)', border: '1px solid var(--mg-border)', color: 'var(--mg-text-secondary)' }}>
        <strong>Tip:</strong> Drag a node onto the canvas to add it. Click to configure. Connect nodes by dragging from&nbsp;the bottom handle to the top of another node.
      </div>
    </div>
  );
};

export default NodePalette;
